import { useImperativeHandle, forwardRef, useEffect } from 'react';
import {
  Box,
  Text,
  Badge,
  HStack,
  Stack,
  Avatar,
  Input,
  createListCollection,
} from '@chakra-ui/react';
import { Separator } from '@/components/ui/separator';
import { Field } from '@/components/ui/field';
import { Switch } from '@/components/ui/switch';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { FaBuilding } from 'react-icons/fa';
import { useUpdateOrganization } from '../organizations.hook';
import { Organization, UpdateOrganizationBody } from '../organizations.types';
import { toaster } from '@/components/ui/toaster';
import { Select } from '@chakra-ui/react';

// Language collection
const languageCollection = createListCollection({
  items: [
    { value: '', label: 'Select Language (Optional)', code: '' },
    { value: 'en', label: 'English', code: 'en' },
    { value: 'nl', label: 'Dutch', code: 'nl' },
    { value: 'de', label: 'German', code: 'de' },
    { value: 'fr', label: 'French', code: 'fr' },
    { value: 'es', label: 'Spanish', code: 'es' },
    { value: 'it', label: 'Italian', code: 'it' },
    { value: 'pt', label: 'Portuguese', code: 'pt' },
    { value: 'ru', label: 'Russian', code: 'ru' },
    { value: 'zh', label: 'Chinese', code: 'zh' },
    { value: 'ja', label: 'Japanese', code: 'ja' },
  ],
  itemToString: (item) => item.label,
  itemToValue: (item) => item.value,
});

const organizationSchema = z.object({
  name: z.string().min(1, 'Organization name is required'),
  description: z.string().optional(),
  website: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val === '') return true;
        // Allow URLs with or without protocol
        try {
          // Try with protocol first
          new URL(val);
          return true;
        } catch {
          // Try adding https:// if no protocol
          try {
            new URL(`https://${val}`);
            return true;
          } catch {
            return false;
          }
        }
      },
      { message: 'Invalid URL format' },
    )
    .or(z.literal('')),
  email: z.string().email('Invalid email format').optional().or(z.literal('')),
  phone: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  postal_code: z.string().optional(),
  country_code: z.string().optional(),
  language_code: z.string().optional(),
  pay_per_use: z.boolean(),
  max_interaction_per_month: z
    .number()
    .min(0, 'Must be a positive number')
    .optional()
    .nullable(),
  max_interaction_per_session: z
    .number()
    .min(0, 'Must be a positive number')
    .optional()
    .nullable(),
});

type OrganizationFormData = z.infer<typeof organizationSchema>;

interface OrganizationEditFormProps {
  organization: Organization;
  onSuccess?: () => void;
  isViewMode?: boolean;
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

export interface OrganizationEditFormRef {
  submit: () => void;
  reset: () => void;
}

export const OrganizationEditForm = forwardRef<
  OrganizationEditFormRef,
  OrganizationEditFormProps
>(
  (
    { organization, onSuccess, isViewMode = false, onSubmittingChange },
    ref,
  ) => {
    const updateOrganizationMutation = useUpdateOrganization();

    const {
      register,
      handleSubmit,
      formState: { errors, isSubmitting },
      reset,
      control,
    } = useForm<OrganizationFormData>({
      resolver: zodResolver(organizationSchema),
      defaultValues: {
        name: organization.name || '',
        description: organization.description || '',
        website: organization.website || '',
        email: organization.email || '',
        phone: organization.phone || '',
        address: organization.address || '',
        city: organization.city || '',
        postal_code: organization.postal_code || '',
        country_code: organization.country_code || '',
        language_code: organization.language_code || '',
        pay_per_use: organization.pay_per_use ?? false,
        max_interaction_per_month:
          organization.max_interaction_per_month || undefined,
        max_interaction_per_session:
          organization.max_interaction_per_session || undefined,
      },
    });

    // Expose submit and reset methods via ref
    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(onSubmit)();
      },
      reset: () => {
        reset();
      },
    }));

    // Update parent component when submitting state changes
    useEffect(() => {
      onSubmittingChange?.(isSubmitting);
    }, [isSubmitting, onSubmittingChange]);

    const onSubmit = async (data: OrganizationFormData) => {
      try {
        const updateData: UpdateOrganizationBody = {
          name: data.name,
          description: data.description || undefined,
          website: data.website || undefined,
          email: data.email || undefined,
          phone: data.phone || undefined,
          address: data.address || undefined,
          city: data.city || undefined,
          postal_code: data.postal_code || undefined,
          country_code: data.country_code || undefined,
          language_code: data.language_code || undefined,
          pay_per_use: data.pay_per_use,
          max_interaction_per_month:
            data.max_interaction_per_month || undefined,
          max_interaction_per_session:
            data.max_interaction_per_session || undefined,
        };

        await updateOrganizationMutation.mutateAsync({
          id: organization.id,
          data: updateData,
        });

        toaster.create({
          title: 'Organization Updated',
          description: 'Organization has been updated successfully',
          type: 'success',
        });

        onSuccess?.();
      } catch (error) {
        console.error('Failed to update organization:', error);

        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to update organization. Please try again.';

        toaster.create({
          title: 'Update Failed',
          description: errorMessage,
          type: 'error',
        });
      }
    };

    const getStatusColor = (status: string): string => {
      return status === 'active' ? 'blue' : 'gray';
    };

    return (
      <Box as="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={6} align="stretch">
          {/* Header Section */}
          <Box>
            <HStack gap={4} mb={4}>
              <Avatar.Root size="md" bg="blue.500" color="white">
                <Avatar.Fallback>
                  <FaBuilding size={20} />
                </Avatar.Fallback>
              </Avatar.Root>
              <Stack direction="column" align="start" gap={1}>
                <Text fontSize="lg" fontWeight="semibold">
                  {organization.name}
                </Text>
                <HStack>
                  <Badge
                    colorScheme={getStatusColor(
                      organization.status || 'active',
                    )}
                    variant="subtle"
                    size="sm"
                  >
                    {organization.status || 'Active'}
                  </Badge>
                </HStack>
              </Stack>
            </HStack>

            <HStack gap={6} fontSize="sm" color="gray.600">
              {organization.created_at && (
                <Text>
                  Created:{' '}
                  {format(new Date(organization.created_at), 'MMM dd, yyyy')}
                </Text>
              )}
              {organization.updated_at && (
                <Text>
                  Updated:{' '}
                  {format(new Date(organization.updated_at), 'MMM dd, yyyy')}
                </Text>
              )}
            </HStack>
          </Box>

          <Separator />

          {/* Organization Information */}
          <Box>
            <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={4}>
              Organization Information
            </Text>
            <Stack gap={4}>
              <Field
                label="Organization Name"
                required
                invalid={!!errors.name}
                errorText={errors.name?.message}
              >
                <Input
                  {...register('name')}
                  disabled={isViewMode}
                  placeholder="Enter organization name"
                  borderRadius="md"
                />
              </Field>

              <Field
                label="Description"
                invalid={!!errors.description}
                errorText={errors.description?.message}
              >
                <Input
                  {...register('description')}
                  disabled={isViewMode}
                  placeholder="Enter description"
                  borderRadius="md"
                />
              </Field>

              <HStack gap={4}>
                <Field
                  label="Email"
                  invalid={!!errors.email}
                  errorText={errors.email?.message}
                  flex={1}
                >
                  <Input
                    {...register('email')}
                    disabled={isViewMode}
                    placeholder="Enter email address"
                    type="email"
                    borderRadius="md"
                  />
                </Field>

                <Field
                  label="Phone"
                  invalid={!!errors.phone}
                  errorText={errors.phone?.message}
                  flex={1}
                >
                  <Input
                    {...register('phone')}
                    disabled={isViewMode}
                    placeholder="Enter phone number"
                    borderRadius="md"
                  />
                </Field>
              </HStack>

              <Field
                label="Website"
                invalid={!!errors.website}
                errorText={errors.website?.message}
              >
                <Input
                  {...register('website')}
                  disabled={isViewMode}
                  placeholder="Enter website URL"
                  type="url"
                  borderRadius="md"
                />
              </Field>
            </Stack>
          </Box>

          <Separator />

          {/* Address Information */}
          <Box>
            <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={4}>
              Address Information
            </Text>
            <Stack gap={4}>
              <Field
                label="Address"
                invalid={!!errors.address}
                errorText={errors.address?.message}
              >
                <Input
                  {...register('address')}
                  disabled={isViewMode}
                  placeholder="Enter address"
                  borderRadius="md"
                />
              </Field>

              <HStack gap={4}>
                <Field
                  label="City"
                  invalid={!!errors.city}
                  errorText={errors.city?.message}
                  flex={1}
                >
                  <Input
                    {...register('city')}
                    disabled={isViewMode}
                    placeholder="Enter city"
                    borderRadius="md"
                  />
                </Field>

                <Field
                  label="Postal Code"
                  invalid={!!errors.postal_code}
                  errorText={errors.postal_code?.message}
                  flex={1}
                >
                  <Input
                    {...register('postal_code')}
                    disabled={isViewMode}
                    placeholder="Enter postal code"
                    borderRadius="md"
                  />
                </Field>
              </HStack>

              <HStack gap={4}>
                <Field
                  label="Country Code"
                  invalid={!!errors.country_code}
                  errorText={errors.country_code?.message}
                  flex={1}
                >
                  <Input
                    {...register('country_code')}
                    disabled={isViewMode}
                    placeholder="Enter country code (e.g., US, UK)"
                    borderRadius="md"
                  />
                </Field>

                <Field
                  label="Language"
                  invalid={!!errors.language_code}
                  errorText={errors.language_code?.message}
                  flex={1}
                >
                  <Controller
                    name="language_code"
                    control={control}
                    render={({ field }) => (
                      <Select.Root
                        collection={languageCollection}
                        size="sm"
                        width="100%"
                        positioning={{ sameWidth: true, strategy: 'fixed' }}
                        value={field.value ? [field.value] : []}
                        onValueChange={(e) => {
                          field.onChange(e.value[0] || '');
                        }}
                        disabled={isViewMode}
                      >
                        <Select.HiddenSelect />
                        <Select.Control>
                          <Select.Trigger>
                            <Select.ValueText placeholder="Select language" />
                          </Select.Trigger>
                          <Select.IndicatorGroup>
                            <Select.Indicator />
                          </Select.IndicatorGroup>
                        </Select.Control>
                        <Select.Positioner>
                          <Select.Content>
                            {languageCollection.items.map((lang) => (
                              <Select.Item key={lang.value} item={lang}>
                                {lang.label}
                                <Select.ItemIndicator />
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Positioner>
                      </Select.Root>
                    )}
                  />
                </Field>
              </HStack>
            </Stack>
          </Box>

          <Separator />

          {/* Usage Configuration */}
          <Box>
            <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={4}>
              Usage Configuration
            </Text>
            <Stack gap={4}>
              <Field label="Pay Per Use">
                <HStack>
                  <Controller
                    control={control}
                    name="pay_per_use"
                    render={({ field }) => (
                      <Switch
                        name="pay_per_use"
                        checked={field.value}
                        onCheckedChange={(details) => {
                          field.onChange(details.checked);
                        }}
                        disabled={isViewMode}
                      />
                    )}
                  />
                  <Text fontSize="sm" color="gray.600">
                    No limit use indication
                  </Text>
                </HStack>
              </Field>
              <HStack gap={4} mb={4}>
                <Field
                  label="Max Interactions Per Month"
                  invalid={!!errors.max_interaction_per_month}
                  errorText={errors.max_interaction_per_month?.message}
                  flex={1}
                >
                  <Input
                    {...register('max_interaction_per_month', {
                      valueAsNumber: true,
                    })}
                    disabled={isViewMode}
                    placeholder="Enter max interactions per month"
                    type="number"
                    min="0"
                    borderRadius="md"
                  />
                </Field>
                <Field
                  label="Max Interactions Per Session"
                  invalid={!!errors.max_interaction_per_session}
                  errorText={errors.max_interaction_per_session?.message}
                  flex={1}
                >
                  <Input
                    {...register('max_interaction_per_session', {
                      valueAsNumber: true,
                    })}
                    disabled={isViewMode}
                    placeholder="Enter max interactions per session"
                    type="number"
                    min="0"
                    borderRadius="md"
                  />
                </Field>
              </HStack>
            </Stack>
          </Box>
        </Stack>
      </Box>
    );
  },
);

OrganizationEditForm.displayName = 'OrganizationEditForm';
